/**
 * Sloop Luxury Theme interactions
 * Designed and developed by Design Flow Digitals
 * https://designflowdigitals.com | support@designflowdigitals.com
 */
import { CartLinesUpdateEvent } from '@shopify/events';
import { morphSection } from '@theme/section-renderer';

(() => {
  const header = document.querySelector('[data-sloop-header]');
  const burger = document.querySelector('[data-sloop-burger]');
  const mnav = document.querySelector('[data-sloop-mnav]');

  const setHeaderOffset = () => {
    const group = document.querySelector('#header-group');
    if (!group) return;
    const height = group.offsetHeight;
    document.body.style.setProperty('--header-group-height', `${height}px`);
    document.body.style.setProperty('--header-height', `${header ? header.offsetHeight : 0}px`);
    if (header && !header.classList.contains('sloop-lux-header--solid')) {
      document.body.style.setProperty('--transparent-header-offset-boolean', '1');
    }
  };

  if (header) {
    const onScroll = () => {
      header.classList.toggle('scrolled', window.scrollY > 40);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', setHeaderOffset);
    setHeaderOffset();
  }

  if (burger && mnav && header) {
    burger.addEventListener('click', () => {
      const open = mnav.classList.toggle('open');
      header.classList.toggle('menu', open);
      burger.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
    mnav.querySelectorAll('a').forEach((a) => {
      a.addEventListener('click', () => {
        mnav.classList.remove('open');
        header.classList.remove('menu');
        burger.setAttribute('aria-expanded', 'false');
      });
    });
  }

  // Catalog mega menu — keep open while moving from trigger to panel
  // (full-width panel; CSS :hover alone drops when crossing the brand/gap)
  document.querySelectorAll('[data-sloop-mega]').forEach((item) => {
    const trigger = item.querySelector('.sloop-lux-nav-link');
    if (!trigger) return;

    let closeTimer = 0;

    const openMega = () => {
      window.clearTimeout(closeTimer);
      item.classList.add('is-open');
      trigger.setAttribute('aria-expanded', 'true');
    };

    const scheduleClose = () => {
      window.clearTimeout(closeTimer);
      closeTimer = window.setTimeout(() => {
        item.classList.remove('is-open');
        trigger.setAttribute('aria-expanded', 'false');
      }, 350);
    };

    item.addEventListener('mouseenter', openMega);
    item.addEventListener('mouseleave', scheduleClose);
    trigger.addEventListener('focus', openMega);
    item.addEventListener('focusout', (event) => {
      if (!item.contains(event.relatedTarget)) scheduleClose();
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && item.classList.contains('is-open')) {
        window.clearTimeout(closeTimer);
        item.classList.remove('is-open');
        trigger.setAttribute('aria-expanded', 'false');
        trigger.focus();
      }
    });
  });

  // Collection filters drawer (mobile)
  const filters = document.querySelector('[data-sloop-filters]');
  const filterScrim = document.querySelector('[data-sloop-filter-scrim]');
  const openFilters = (open) => {
    if (!filters) return;
    filters.classList.toggle('open', open);
    if (filterScrim) {
      filterScrim.hidden = !open;
      filterScrim.classList.toggle('on', open);
    }
    document.documentElement.classList.toggle('sloop-filter-open', open);
  };
  document.querySelectorAll('[data-sloop-filter-open]').forEach((btn) => {
    btn.addEventListener('click', () => openFilters(true));
  });
  document.querySelectorAll('[data-sloop-filter-close]').forEach((btn) => {
    btn.addEventListener('click', () => openFilters(false));
  });
  filterScrim?.addEventListener('click', () => openFilters(false));
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') openFilters(false);
  });

  // Wishlist heart (local)
  try {
    const wishKey = 'sloop-wishlist';
    const wished = new Set(JSON.parse(localStorage.getItem(wishKey) || '[]'));
    document.querySelectorAll('[data-sloop-wish]').forEach((btn) => {
      const id = btn.getAttribute('data-sloop-wish');
      if (wished.has(id)) btn.classList.add('on');
      btn.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (wished.has(id)) wished.delete(id);
        else wished.add(id);
        btn.classList.toggle('on', wished.has(id));
        localStorage.setItem(wishKey, JSON.stringify([...wished]));
      });
    });
  } catch (_) {
    /* ignore */
  }

  // Scroll reveal
  const reveals = document.querySelectorAll('.sloop-lux .reveal');
  if (reveals.length && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.14 }
    );
    reveals.forEach((el) => io.observe(el));
  } else {
    reveals.forEach((el) => el.classList.add('in'));
  }

  // FAQ accordion
  document.querySelectorAll('.sloop-lux-faq__q').forEach((q) => {
    q.addEventListener('click', () => {
      const item = q.parentElement;
      const answer = q.nextElementSibling;
      const open = item.classList.contains('open');

      document.querySelectorAll('.sloop-lux-faq__item.open').forEach((o) => {
        o.classList.remove('open');
        const a = o.querySelector('.sloop-lux-faq__a');
        if (a) a.style.maxHeight = null;
        const btn = o.querySelector('.sloop-lux-faq__q');
        if (btn) btn.setAttribute('aria-expanded', 'false');
      });

      if (!open) {
        item.classList.add('open');
        answer.style.maxHeight = answer.scrollHeight + 'px';
        q.setAttribute('aria-expanded', 'true');
      }
    });
  });

  // Arrivals rail
  document.querySelectorAll('[data-sloop-rail]').forEach((railWrap) => {
    const track = railWrap.querySelector('[data-sloop-rail-track]');
    const prev = railWrap.querySelector('[data-sloop-rail-prev]');
    const next = railWrap.querySelector('[data-sloop-rail-next]');
    if (!track) return;

    const step = () => {
      const card = track.querySelector('.sloop-lux-prod, .sloop-lux-pcard');
      if (!card) return Math.min(track.clientWidth * 0.8, 320);
      const styles = window.getComputedStyle(track);
      const gap = parseFloat(styles.columnGap || styles.gap) || 26;
      return card.getBoundingClientRect().width + gap;
    };

    prev?.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
    next?.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
  });

  // Product gallery — main stage + thumbnails
  document.querySelectorAll('[data-sloop-gallery]').forEach((gallery) => {
    const thumbs = gallery.querySelectorAll('[data-thumb-for]');
    const slides = gallery.querySelectorAll('[data-media-id]');
    if (!thumbs.length || !slides.length) return;

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const id = thumb.getAttribute('data-thumb-for');
        thumbs.forEach((t) => {
          const on = t === thumb;
          t.classList.toggle('is-active', on);
          t.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        slides.forEach((slide) => {
          const match = slide.getAttribute('data-media-id') === id;
          slide.classList.toggle('is-active', match);
          if (match) slide.removeAttribute('hidden');
          else slide.setAttribute('hidden', '');
        });
      });
    });
  });

  // Quantity steppers
  document.querySelectorAll('[data-sloop-qty]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const wrap = btn.closest('.sloop-lux-qty');
      const input = wrap?.querySelector('[data-sloop-qty-input]');
      if (!input) return;
      const delta = Number(btn.getAttribute('data-sloop-qty') || 0);
      const next = Math.max(1, (Number(input.value) || 1) + delta);
      input.value = String(next);
    });
  });

  const cartAddUrl = () =>
    (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) ||
    (window.Shopify && window.Shopify.routes && `${window.Shopify.routes.root}cart/add.js`) ||
    '/cart/add.js';

  const collectCartSectionIds = () => {
    const ids = new Set(['cart-drawer-section']);
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
    });
    return Array.from(ids);
  };

  const morphCartSections = async (sections) => {
    if (!sections) return;
    for (const [sectionId, html] of Object.entries(sections)) {
      if (!html) continue;
      try {
        await morphSection(sectionId, html);
      } catch (err) {
        const existing = document.getElementById(`shopify-section-${sectionId}`);
        if (existing) {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const next =
            doc.getElementById(`shopify-section-${sectionId}`) ||
            doc.querySelector('.shopify-section') ||
            doc.body.firstElementChild;
          if (next) existing.replaceWith(next);
        }
      }
    }
  };

  const openCartDrawer = async () => {
    document.querySelector('.page-wrapper')?.classList.remove('page-wrapper--drawer-open');

    const drawer = document.querySelector('theme-drawer#cart-drawer');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
      const panel = drawer.querySelector('dialog');
      if (panel && panel.open && typeof panel.showModal === 'function' && !panel.matches(':modal')) {
        try {
          panel.close();
          panel.showModal();
        } catch (e) {
          /* ignore */
        }
      }
      return;
    }
    if (window.Shopify?.actions?.openCart) {
      await window.Shopify.actions.openCart();
      return;
    }
    window.location.href = (window.Theme && window.Theme.routes && window.Theme.routes.cart_url) || '/cart';
  };

  const refreshCartAjax = async () => {
    const root = window.Shopify?.routes?.root || '/';
    const res = await fetch(`${root}cart.js`, {
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) throw new Error('Cart fetch failed');
    return res.json();
  };

  /**
   * Horizon-compatible add-to-cart: request section HTML, morph drawer, emit cart event, open overlay.
   */
  const addToCart = async ({ variantId, quantity = 1, formData = null, productId = null }) => {
    const sectionIds = collectCartSectionIds();
    const deferred = CartLinesUpdateEvent.createPromise();

    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(variantId), quantity }],
        promise: deferred.promise,
      })
    );

    let body;
    let headers = { Accept: 'application/json' };

    if (formData) {
      formData.set('id', String(variantId));
      formData.set('quantity', String(quantity));
      formData.set('sections', sectionIds.join(','));
      formData.set('sections_url', window.location.pathname);
      body = formData;
    } else {
      headers['Content-Type'] = 'application/json';
      body = JSON.stringify({
        id: Number(variantId),
        quantity,
        sections: sectionIds.join(','),
        sections_url: window.location.pathname,
      });
    }

    const res = await fetch(cartAddUrl(), {
      method: 'POST',
      headers,
      body,
    });

    const data = await res.json();
    if (data.status) {
      const ajaxCart = await refreshCartAjax().catch(() => null);
      deferred.resolve({
        cart: ajaxCart ? CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart) : undefined,
        detail: {
          didError: true,
          items: ajaxCart?.items || [],
          source: 'sloop-luxury',
          itemCount: quantity,
          productId,
        },
      });
      throw new Error(data.description || data.message || 'Could not add to cart');
    }

    await morphCartSections(data.sections);

    // If sections missing, re-fetch drawer section
    if (!data.sections || !data.sections['cart-drawer-section']) {
      try {
        const sectionRes = await fetch(
          `${window.Shopify?.routes?.root || '/'}?sections=cart-drawer-section`
        );
        const sectionData = await sectionRes.json();
        await morphCartSections(sectionData);
      } catch (e) {
        /* continue */
      }
    }

    const ajaxCart = await refreshCartAjax();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
      detail: {
        didError: false,
        items: ajaxCart.items,
        source: 'sloop-luxury',
        itemCount: quantity,
        productId,
        sections: data.sections,
      },
    });

    await openCartDrawer();
    return data;
  };

  // Variant + AJAX add to cart
  document.querySelectorAll('[data-sloop-product-form]').forEach((form) => {
    if (form.dataset.sloopBound === '1') return;
    form.dataset.sloopBound = '1';

    const pdp = form.closest('[data-sloop-pdp]');
    const variantInput = form.querySelector('[data-sloop-variant-id]');
    const optionSelects = form.querySelectorAll('[data-sloop-option]');
    const variantsEl = form.querySelector('[data-sloop-variants]');
    const atc = form.querySelector('[data-sloop-atc]');
    const atcLabel = form.querySelector('[data-sloop-atc-label]');
    const priceEl = pdp?.querySelector('[data-sloop-price]');
    const statusEl = form.querySelector('[data-sloop-form-status]');
    const skuValue = pdp?.querySelector('[data-sloop-sku-value]');

    const formatMoney = (cents) => {
      if (window.Shopify?.formatMoney) {
        return window.Shopify.formatMoney(
          cents,
          window.theme?.moneyFormat || window.Shopify.money_format || '${{amount}}'
        );
      }
      try {
        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD',
        }).format(cents / 100);
      } catch (e) {
        return `$${(cents / 100).toFixed(2)}`;
      }
    };

    let variants = [];
    if (variantsEl) {
      try {
        variants = JSON.parse(variantsEl.textContent || '[]');
      } catch (e) {
        variants = [];
      }
    }

    const selectedOptions = () => Array.from(optionSelects).map((select) => select.value);

    const findVariant = () => {
      if (!variants.length) return null;
      const opts = selectedOptions();
      return variants.find((v) => v.options.every((opt, i) => String(opt) === String(opts[i]))) || null;
    };

    const syncVariant = () => {
      const variant = findVariant();
      if (!variant || !variantInput) return;

      variantInput.value = variant.id;

      if (priceEl) {
        if (variant.compare_at_price > variant.price) {
          priceEl.innerHTML = `<span class="sloop-lux-pdp__price-now">${formatMoney(
            variant.price
          )}</span><s class="sloop-lux-pdp__price-was">${formatMoney(variant.compare_at_price)}</s>`;
        } else {
          priceEl.innerHTML = `<span class="sloop-lux-pdp__price-now">${formatMoney(variant.price)}</span>`;
        }
      }

      if (skuValue) skuValue.textContent = variant.sku || '';

      if (atc) {
        atc.disabled = !variant.available;
        if (atcLabel) {
          atcLabel.textContent = variant.available ? atcLabel.dataset.label || 'Add to bag' : 'Sold out';
        }
      }

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    };

    if (atcLabel && !atcLabel.dataset.label) {
      atcLabel.dataset.label = atcLabel.textContent.trim();
    }

    optionSelects.forEach((select) => {
      select.addEventListener('change', syncVariant);
    });

    form.querySelectorAll('[data-sloop-swatch]').forEach((swatch) => {
      swatch.addEventListener('click', () => {
        const index = swatch.getAttribute('data-option-index');
        const value = swatch.getAttribute('data-value');
        const select = form.querySelectorAll('[data-sloop-option]')[Number(index)];
        if (!select) return;
        select.value = value;
        const group = swatch.closest('[data-sloop-swatches]');
        group?.querySelectorAll('[data-sloop-swatch]').forEach((s) => {
          const on = s === swatch;
          s.classList.toggle('is-active', on);
          s.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        select.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (!atc || atc.disabled) return;

      const variantId = variantInput?.value || form.querySelector('[name="id"]')?.value;
      const qty = Number(form.querySelector('[name="quantity"]')?.value || 1);
      if (!variantId) {
        if (statusEl) {
          statusEl.hidden = false;
          statusEl.textContent = 'Select options first';
        }
        return;
      }

      if (statusEl) {
        statusEl.hidden = false;
        statusEl.textContent = 'Adding…';
      }
      atc.disabled = true;

      try {
        await addToCart({
          variantId,
          quantity: qty,
          formData: new FormData(form),
          productId: pdp?.getAttribute('data-product-id'),
        });
        if (statusEl) statusEl.textContent = 'Added to bag';
      } catch (err) {
        console.error('[sloop] add to cart failed', err);
        if (statusEl) statusEl.textContent = err?.message || 'Could not add — please try again.';
      } finally {
        const variant = findVariant();
        atc.disabled = variant ? !variant.available : false;
        if (!variants.length) atc.disabled = false;
        setTimeout(() => {
          if (statusEl) statusEl.hidden = true;
        }, 2400);
      }
    });
  });

  // Complete-the-look quick add
  document.querySelectorAll('[data-sloop-upsell-add]').forEach((btn) => {
    if (btn.dataset.sloopBound === '1') return;
    btn.dataset.sloopBound = '1';

    btn.addEventListener('click', async () => {
      const id = btn.getAttribute('data-variant-id');
      if (!id || btn.disabled) return;
      const label = btn.textContent;
      btn.disabled = true;
      btn.textContent = '…';
      try {
        await addToCart({ variantId: id, quantity: 1 });
        btn.textContent = 'Added';
      } catch (e) {
        console.error('[sloop] upsell add failed', e);
        btn.textContent = 'Retry';
        btn.disabled = false;
        return;
      }
      setTimeout(() => {
        btn.textContent = label;
        btn.disabled = false;
      }, 1600);
    });
  });

  // Product card quick add
  document.querySelectorAll('[data-sloop-quick-add]').forEach((btn) => {
    if (btn.dataset.sloopBound === '1') return;
    btn.dataset.sloopBound = '1';

    btn.addEventListener('click', async (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (btn.disabled) return;

      const id = btn.getAttribute('data-variant-id');
      const label = btn.textContent.trim();
      if (!id) {
        const url = btn.getAttribute('data-product-url');
        if (url) window.location.href = url;
        return;
      }

      btn.disabled = true;
      btn.textContent = 'Adding…';
      try {
        await addToCart({ variantId: id, quantity: 1 });
        btn.textContent = 'Added';
      } catch (e) {
        console.error('[sloop] quick add failed', e);
        btn.textContent = 'Retry';
        btn.disabled = false;
        return;
      }
      setTimeout(() => {
        btn.textContent = label;
        btn.disabled = false;
      }, 1600);
    });
  });

  // Sticky add to cart on product page
  document.querySelectorAll('[data-sloop-sticky-atc]').forEach((bar) => {
    const pdp = bar.closest('[data-sloop-pdp]') || document.querySelector('[data-sloop-pdp]');
    const anchor = pdp?.querySelector('[data-sloop-atc-anchor]');
    const stickyBtn = bar.querySelector('[data-sloop-sticky-btn]');
    const stickyLabel = bar.querySelector('[data-sloop-sticky-label]');
    const stickyPrice = bar.querySelector('[data-sloop-sticky-price]');
    const form = pdp?.querySelector('[data-sloop-product-form]');
    const mainAtc = pdp?.querySelector('[data-sloop-atc]');
    const mainLabel = pdp?.querySelector('[data-sloop-atc-label]');
    const priceEl = pdp?.querySelector('[data-sloop-price]');

    bar.hidden = false;

    const syncStickyFromMain = () => {
      if (stickyPrice && priceEl) {
        const now = priceEl.querySelector('.sloop-lux-pdp__price-now');
        stickyPrice.textContent = now ? now.textContent.trim() : priceEl.textContent.trim();
      }
      if (stickyBtn && mainAtc) {
        stickyBtn.disabled = mainAtc.disabled;
      }
      if (stickyLabel && mainLabel) {
        stickyLabel.textContent = mainLabel.textContent.trim();
      }
    };

    syncStickyFromMain();

    if (anchor && 'IntersectionObserver' in window) {
      const io = new IntersectionObserver(
        ([entry]) => {
          const show = !entry.isIntersecting;
          bar.classList.toggle('is-visible', show);
          bar.setAttribute('aria-hidden', show ? 'false' : 'true');
        },
        { threshold: 0, rootMargin: '-8px 0px 0px 0px' }
      );
      io.observe(anchor);
    } else {
      bar.classList.add('is-visible');
      bar.setAttribute('aria-hidden', 'false');
    }

    // Keep sticky price/label in sync when variants change
    pdp?.querySelectorAll('[data-sloop-option]').forEach((select) => {
      select.addEventListener('change', () => setTimeout(syncStickyFromMain, 0));
    });
    pdp?.querySelectorAll('[data-sloop-swatch]').forEach((swatch) => {
      swatch.addEventListener('click', () => setTimeout(syncStickyFromMain, 0));
    });

    stickyBtn?.addEventListener('click', () => {
      if (stickyBtn.disabled) return;
      if (form) {
        form.requestSubmit ? form.requestSubmit() : form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
      } else if (mainAtc) {
        mainAtc.click();
      }
    });
  });
})();
